import axios from 'axios';
import { API_CONFIG } from '../config/api.config';
import { LoginCredentials, AuthResponse } from '../types/auth';
import { store } from '../store/store';
import { setUser, logOut } from '../store/slices/authSlice';
import { startLoading, stopLoading } from '../store/slices/loadingSlice';

// Create separate instances for auth and data APIs
const authAxios = axios.create({
  baseURL: 'https://auth.ts1cloud.com',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
  timeout: 30000
});

const dataApi = axios.create({
  ...API_CONFIG,
  retry: 3,
  retryDelay: (retryCount) => retryCount * 1000
});

// Add request interceptor to set company headers
dataApi.interceptors.request.use((config) => {
  const state = store.getState();
  const user = state.auth.user;

  if (user?.companyID && user?.companyName) {
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['x-company-id'] = user.companyID.toString();
    config.headers['x-company-name'] = user.companyName;
    config.headers['Origin'] = window.location.origin;
  } else {
    console.warn('Missing company information in user data');
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add retry interceptor for data API
dataApi.interceptors.response.use(undefined, async (err) => {
  const config = err.config;
  if (!config || !config.retry) return Promise.reject(err);
  
  config.retryCount = config.retryCount || 0;
  
  if (config.retryCount >= config.retry) {
    return Promise.reject(err);
  }
  
  config.retryCount += 1;
  const delayRetry = new Promise(resolve => setTimeout(resolve, config.retryDelay(config.retryCount)));
  await delayRetry;
  return dataApi(config);
});

// Session timeout handling
let sessionTimeoutId: number;
let verificationIntervalId: number;

const SESSION_DURATION = 24 * 60 * 60 * 1000;
const VERIFICATION_INTERVAL = 30 * 60 * 1000;

// Add loading state management to data API
dataApi.interceptors.request.use(config => {
  store.dispatch(startLoading());
  
  const state = store.getState();
  const user = state.auth.user;

  if (user?.companyID && user?.companyName) {
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept'] = 'application/json';
    config.headers['x-company-id'] = user.companyID.toString();
    config.headers['x-company-name'] = user.companyName;
    config.headers['Origin'] = window.location.origin;
  }

  return config;
});

dataApi.interceptors.response.use(
  (response) => {
    store.dispatch(stopLoading());
    return response;
  },
  async (error) => {
    store.dispatch(stopLoading());
    if (error.response?.status === 401) {
      try {
        const response = await authApi.verifyAccess();
        if (response.message === 'Valid Session' && response.data) {
          startSessionTimeout();
          store.dispatch(setUser({
            companyID: response.data.companyID,
            companyName: response.data.companyName,
            roles: ['user']
          }));
          return dataApi(error.config);
        }
      } catch (verifyError) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }

    if (error.response?.status === 403) {
      window.dispatchEvent(new CustomEvent('auth:forbidden'));
    }

    return Promise.reject(error);
  }
);

export const startSessionTimeout = () => {
  clearSessionTimeout();
  sessionTimeoutId = window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('auth:timeout'));
  }, SESSION_DURATION);

  verificationIntervalId = window.setInterval(async () => {
    try {
      await authApi.verifyAccess();
    } catch (error) {
      console.error('Session verification failed:', error);
      window.dispatchEvent(new CustomEvent('auth:timeout'));
    }
  }, VERIFICATION_INTERVAL);
};

export const clearSessionTimeout = () => {
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }
  if (verificationIntervalId) {
    clearInterval(verificationIntervalId);
  }
};

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await authAxios.post<AuthResponse>('/auth/login', credentials);
      startSessionTimeout();
      
      // Map user data consistently
      const userData = {
        companyID: response.data.userData.companyID,
        companyName: response.data.userData.companyName,
        username: response.data.userData.username,
        email: response.data.userData.email,
        roles: response.data.userData.roles[0] === 'admin' ? '1' : '2'
      };
      
      store.dispatch(setUser(userData));
      return response.data;
    } catch (error) {
      console.error('Login Error:', error);
      throw error;
    }
  },
  
  logout: async () => {
    try {
      clearSessionTimeout();
      await authAxios.post('/auth/logout', {}, { withCredentials: true });
      
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear Redux store
      store.dispatch(logOut());
      
      return { success: true };
    } catch (error) {
      console.error('Logout Error:', error);
      // Even if the API call fails, we should still clear local state
      localStorage.clear();
      sessionStorage.clear();
      store.dispatch(logOut());
      throw error;
    }
  },
  
  verifyAccess: async () => {
    try {
      const response = await authAxios.get('/auth/verifyAccess', {
        withCredentials: true
      });
      
      if (response.data?.message === 'Valid Session' && response.data?.data) {
        startSessionTimeout();
        
        // Map user data consistently here as well
        const userData = {
          companyID: response.data.data.companyID,
          companyName: response.data.data.companyName,
          username: response.data.data.username,
          email: response.data.data.email,
          roles: response.data.data.roles?.includes('admin') ? '1' : '2'
        };
        
        store.dispatch(setUser(userData));
      }
      
      return response.data;
    } catch (error) {
      console.error('Verify Access Error:', error);
      throw error;
    }
  },
  
  updatePassword: async (companyName: string, email: string, currentPassword: string, newPassword: string) => {
    try {
      const response = await authAxios.post('/auth/updatePassword', {
        companyName,
        email,
        currentPassword,
        newPassword
      }, {
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }
};

// Export the data API for other services
export const api = dataApi;